import * as React from "react";
import { Button, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useI18n } from 'src/utils/lni18n';
import recorderService, { RecorderStates, TestStates, gainAtom, recordingAtom, selectedMicAtom, testRecordingAtom } from "./Recoderservice";
import { RecorderProgress } from "./RecorderProgress";
import { VuMeter } from "./VuMeter/VuMeter";
import { atom, useAtom, useAtomValue } from "jotai";
import classes from "../lecture.module.scss"
import { VerticalProgress } from "src/components/VerticalProgress/VerticalProgress";
import KeyboardEventHandler from "@infinium/react-keyboard-event-handler";
import LnIcon from "src/components/LnIcon";


const hasRecordingAtom = atom((get) => {
    const a = get(recordingAtom);
    return a.state === RecorderStates.FINISHED || a.state === RecorderStates.PAUSED;
});

export enum RecordingAction {
    save = 1,
    editBeforeSave = 2,
}

const canRecordAtom = atom((get) => {
    const recAtom = get(recordingAtom);
    const testAtom = get(testRecordingAtom);
    return (testAtom.state === TestStates.RECORDING || testAtom.state === TestStates.PLAYING
        || recAtom.state === RecorderStates.RECORDING  ) === false;
});




export const RecorderDialog: React.FC<{ onSubmit: (d: RecordingAction) => void, onCancel: () => void }> = ({ onSubmit, onCancel }) => {

    const { languageService: t } = useI18n();

    const [gain, setGain] = useAtom(gainAtom);
    const [recordingState] = useAtom(recordingAtom);
    const [hasRecording] = useAtom(hasRecordingAtom);
    const [selectedMic, setSelectedMic] = useAtom(selectedMicAtom);
    const [testState] = useAtom(testRecordingAtom);
    const [microphones, setMicrophones] = React.useState<MediaDeviceInfo[]>([]);

    const [countDownTimer, setCountDownTimer] = React.useState(100);
    const [showTimer, setShowTimer] = React.useState(false);

    const handleSubmit = (action: RecordingAction) => {
        onSubmit(action);
    }

    const canRecord = useAtomValue(canRecordAtom);


    React.useEffect(() => {
        let isMounted = true;
        recorderService.clearRecording();
        recorderService.dialogOpen = true;
        loadDevices(isMounted);
        return () => {
            isMounted = false;
            recorderService.cleanupTestRecording();
            recorderService.stopRecord();
            recorderService.dialogOpen = false;
        }
    }, [])


    const loadDevices = async (isMounted: boolean) => {
        if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
            return;
        }
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            if (isMounted) {
                setMicrophones(devices.filter(d => d.kind === "audioinput"));
            }
        } catch {
            // Ignore errors from enumerateDevices to avoid unhandled promise rejections
        }
    };


    const startRecording = React.useCallback(() => {

        let num = 100;

        function countDown() {
            if (num > 0) {
                num -= 1;
                setCountDownTimer(num);
                window.setTimeout(countDown, 7);
            }
            else {
                recorderService.startRecord();
                setShowTimer(false);
            }
        }

        setCountDownTimer(100);
        setShowTimer(true);
        countDown();

    }, [setCountDownTimer, setShowTimer, countDownTimer]);


    const handleKeyEvent = React.useCallback((key: string) => {


        if (!canRecord) return;

        switch (key.toLowerCase()) {
            case "s":
                if (recordingState.state === RecorderStates.RECORDING) {
                    recorderService.stopRecord();
                }
                break;

            case "r":
                if (recordingState.state !== RecorderStates.RECORDING) {
                    startRecording();
                }
                break;
        }
    }, [recordingState.state, startRecording, canRecord]);

    return <>
        <KeyboardEventHandler
            handleKeys={['r', 's']}
            onKeyEvent={(key: string, e: any) => handleKeyEvent(key)}
            handleFocusableElements={true} >
        </KeyboardEventHandler>
        <span className="moveable" data-dialog-draggable>
            <ModalHeader>{t.getText("record")}</ModalHeader>
        </span>
        <ModalBody id="recorder">

            {microphones.length > 1 && (<div className="mb-3">
                <div className="mb-3 d-flex">

                    <LnIcon name="mic" className="mr-2" />
                    <select
                        className="form-control"
                        value={selectedMic ?? ""}
                        onChange={(e) => setSelectedMic(e.target.value || undefined)}
                    >
                        <option value="">{t.getText("default")}</option>
                        {microphones.map(mic => (
                            <option key={mic.deviceId} value={mic.deviceId}>
                                {mic.label || `Microphone ${mic.deviceId.slice(0, 8)}`}
                            </option>
                        ))}
                    </select>


                </div>
                <div className="small muted overflow-hidden" >{microphones.find(m => m.deviceId === selectedMic)?.label ?? t.getText("default")}</div>
                <hr />
            </div>

            )}



            <div className="mt-3 mb-2 d-flex justify-content-between">
                {(testState.state === TestStates.IDLE || testState.state === TestStates.READY) &&
                    <button
                        aria-label={t.getText("test.microphone")}
                        className="btn btn-primary btn-small"
                        disabled={recordingState.state === RecorderStates.RECORDING}
                        onClick={() => recorderService.startTestRecord()}
                    >
                        <LnIcon name="mic" className="filter-white" />
                    </button>
                }
                {testState.state === TestStates.RECORDING &&
                    <>
                        <button className="btn btn-inverse btn-small" onClick={() => recorderService.stopTestRecord()}>
                            {t.getText("stop")}
                        </button>
                        <LnIcon className="filter-primary" name="mic" />
                    </>
                }
                {testState.state === TestStates.READY &&
                    <button className="btn btn-primary btn-small"
                        disabled={recordingState.state === RecorderStates.RECORDING}
                        onClick={() => recorderService.playTestRecording()}>
                        {t.getText("play")}
                    </button>
                }
                {testState.state === TestStates.PLAYING &&
                    <button className="btn btn-inverse btn-small" onClick={() => recorderService.stopTestPlayback()}>
                        {t.getText("stop")}
                    </button>
                }

            </div>
            <div>{t.getText("test.microphone")}</div>
            <hr />


            <div className={`${classes.recorderBars} d-flex`}>
                <div className="mr-4">
                    <VuMeter />
                </div>
                <div className="d-flex flex-column align-items-center flex-fill">
                    <div>
                        <button disabled={!canRecord}
                            onClick={startRecording}
                            className="w-100 mb-3 btn btn-primary btn-small"
                            aria-label="Press R key to start recording"
                        >
                            {t.getText("record")} (R)
                        </button>
                    </div>

                    {recordingState.state === RecorderStates.RECORDING &&
                        <button disabled={recordingState.state !== RecorderStates.RECORDING}
                            onClick={recorderService.stopRecord}
                            aria-label="Press S key to stop recording"
                            className={(recordingState.state === RecorderStates.RECORDING ? "" : "invisible ") + " w-100 btn btn-inverse btn-small"} >
                            {t.getText("stop")} (S)
                        </button>
                    }

                    {showTimer &&
                        <div className="flex-fill mt-4 w-100 d-flex flex-column align-items-center">
                            <VerticalProgress className="w-50" min={0} max={100} value={countDownTimer} color="warning" />
                        </div>
                    }
                    {recordingState.state === RecorderStates.RECORDING && <LnIcon className="mt-4 filter-primary" name="mic" />}

                </div>
                <RecorderProgress />
            </div>




            <div className="mt-3">
                <label className="form-label">{t.getText("recording.level")}</label>
                <input min={0.4} max={12} value={gain} type="range" step={0.2} onChange={(e) => setGain(parseFloat(e.target.value))} className="form-range" ></input>
            </div>


        </ModalBody>
        <ModalFooter>
            <div className="d-flex w-100 justify-content-between mb-3">
                <Button disabled={!hasRecording}
                    className="btn btn-primary btn-small" onClick={() => handleSubmit(RecordingAction.save)}>{t.getText("save.short")}</Button>&nbsp;
                <Button id="editNewRecording" disabled={!hasRecording} className="btn btn-primary btn-small" onClick={() => handleSubmit(RecordingAction.editBeforeSave)}>{t.getText("edit")}</Button>
            </div>

            <Button className="btn btn-inverse btn-small" onClick={onCancel}>{t.getText("cancel")}</Button>
        </ModalFooter>
    </>
};
